'use client';
import { useEffect, useRef, useState } from 'react';

/* ============================================================
   ViewSwitch — the matchups/standings toggle as panel hardware.

   Design note: everything on this board is a physical object -- flaps that
   fall, dots that flip. A rounded software pill next to that reads as a
   different product bolted on. So the control that changes what the board
   displays is the thing that would actually do it on a departures board: a
   two-position MODE selector on the console beneath the display.

   Anatomy:
     - a brushed-metal plate, screwed down at both ends (the screws are
       drawn, and their slots sit at different angles -- nobody drives two
       screws to the same clock position)
     - an engraved legend either side, lit by a small indicator lamp
     - a rocker that physically tilts, with a specular highlight that
       tracks the tilt, and a shadow that deepens on the depressed side
     - a detent: it throws with a short travel, not a slow ease, because a
       real switch snaps past center

   Interaction: click anywhere on the plate, or use it as a real control --
   it's a role="switch" with arrow keys, Home/End, and Space/Enter. Focus
   is visible on the plate, not suppressed.

   Reduced motion: the tilt and lamp fade become instant state changes.
   The switch still works; it just stops animating.
   ============================================================ */

const AMBER = '#ffb300';

export default function ViewSwitch({
  value = 'matchups',            // 'matchups' | 'standings'
  onChange,
  labels = ['MATCHUPS', 'STANDINGS'],
  size = 1,                      // scale multiplier
}) {
  const [reduced, setReduced] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  const isRight = value === 'standings';
  const set = (v) => { if (v !== value) onChange?.(v); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'End') { e.preventDefault(); set('standings'); }
    else if (e.key === 'ArrowLeft' || e.key === 'Home') { e.preventDefault(); set('matchups'); }
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault(); set(isRight ? 'matchups' : 'standings');
    }
  };

  // geometry
  const u = (n) => `${n * size}px`;
  const plateH = 46 * size;
  const rockerW = 62 * size;
  const rockerH = 26 * size;
  const tilt = reduced ? 0 : 13;

  return (
    <div
      ref={ref}
      role="switch"
      aria-checked={isRight}
      aria-label={`View: ${isRight ? labels[1] : labels[0]}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onClick={() => set(isRight ? 'matchups' : 'standings')}
      className="vs-plate"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: u(12),
        height: plateH,
        padding: `0 ${u(14)}`,
        borderRadius: u(4),
        cursor: 'pointer',
        userSelect: 'none',
        // brushed metal: a vertical sheen plus fine horizontal grain
        background:
          `linear-gradient(180deg,#2b2e34 0%,#212429 42%,#191c21 58%,#23262c 100%)`,
        boxShadow:
          `inset 0 1px 0 rgba(255,255,255,.09), inset 0 -1px 0 rgba(0,0,0,.7),
           0 2px 6px rgba(0,0,0,.55)`,
        border: '1px solid #101215',
      }}
    >
      <Screw size={size} angle={22} />

      <Legend text={labels[0]} lit={!isRight} size={size} reduced={reduced} align="right" />

      {/* the rocker itself */}
      <span
        style={{
          position: 'relative',
          width: rockerW,
          height: rockerH,
          borderRadius: u(3),
          background: '#0c0d10',
          boxShadow: `inset 0 ${u(2)} ${u(5)} rgba(0,0,0,.9)`,
          flex: '0 0 auto',
          perspective: u(240),
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: u(2),
            borderRadius: u(2),
            transformStyle: 'preserve-3d',
            transform: `rotateX(${isRight ? -tilt : tilt}deg)`,
            transformOrigin: '50% 50%',
            // a switch snaps past its detent -- fast out, tiny overshoot
            transition: reduced ? 'none' : 'transform 130ms cubic-bezier(.2,1.6,.4,1)',
            background: isRight
              ? 'linear-gradient(180deg,#3a3e45 0%,#2a2e34 55%,#41454d 100%)'
              : 'linear-gradient(180deg,#41454d 0%,#2a2e34 45%,#3a3e45 100%)',
            boxShadow: isRight
              ? `inset 0 ${u(-1)} ${u(3)} rgba(0,0,0,.6), 0 ${u(1)} ${u(2)} rgba(0,0,0,.5)`
              : `inset 0 ${u(1)} ${u(3)} rgba(255,255,255,.06), 0 ${u(1)} ${u(2)} rgba(0,0,0,.5)`,
          }}
        >
          {/* specular highlight tracks the tilt */}
          <span
            style={{
              position: 'absolute',
              left: u(6), right: u(6),
              top: isRight ? '58%' : u(3),
              height: u(4),
              borderRadius: u(3),
              background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.16),transparent)',
              transition: reduced ? 'none' : 'top 130ms ease',
            }}
          />
          {/* grip ribs */}
          <span
            style={{
              position: 'absolute', inset: 0, borderRadius: u(2),
              backgroundImage:
                `repeating-linear-gradient(90deg, rgba(0,0,0,.28) 0 ${u(1)}, transparent ${u(1)} ${u(4)})`,
              opacity: .55,
            }}
          />
        </span>
      </span>

      <Legend text={labels[1]} lit={isRight} size={size} reduced={reduced} align="left" />

      <Screw size={size} angle={-64} />

      <style>{`
        .vs-plate:focus-visible{
          outline:2px solid ${AMBER}; outline-offset:3px;
        }
      `}</style>
    </div>
  );
}

function Legend({ text, lit, size, reduced, align }) {
  const u = (n) => `${n * size}px`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: u(6), flexDirection: align === 'right' ? 'row' : 'row-reverse' }}>
      <span
        style={{
          // engraved text: a dark glyph with a 1px light shadow below it
          fontSize: u(9),
          letterSpacing: '.18em',
          fontWeight: 700,
          color: lit ? AMBER : '#6a6f78',
          textShadow: lit
            ? `0 0 ${u(8)} rgba(255,179,0,.45), 0 1px 0 rgba(0,0,0,.9)`
            : `0 1px 0 rgba(255,255,255,.05)`,
          transition: reduced ? 'none' : 'color 160ms ease, text-shadow 160ms ease',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </span>
      {/* indicator lamp */}
      <span
        style={{
          width: u(5), height: u(5), borderRadius: '50%', flex: '0 0 auto',
          background: lit ? AMBER : '#1b1e23',
          boxShadow: lit
            ? `0 0 ${u(6)} ${u(1)} rgba(255,179,0,.55), inset 0 0 ${u(2)} rgba(255,255,255,.5)`
            : 'inset 0 1px 1px rgba(0,0,0,.8)',
          transition: reduced ? 'none' : 'background 160ms ease, box-shadow 160ms ease',
        }}
      />
    </span>
  );
}

function Screw({ size, angle }) {
  const d = 7 * size;
  return (
    <span
      aria-hidden="true"
      style={{
        width: d, height: d, borderRadius: '50%', flex: '0 0 auto',
        background: 'radial-gradient(circle at 35% 30%,#4a4e55,#22252a 70%)',
        boxShadow: 'inset 0 -1px 1px rgba(0,0,0,.7), 0 1px 0 rgba(255,255,255,.05)',
        position: 'relative',
      }}
    >
      <span
        style={{
          position: 'absolute', left: '50%', top: '50%',
          width: d * 0.62, height: Math.max(1, size),
          background: '#0e1013',
          transform: `translate(-50%,-50%) rotate(${angle}deg)`,
          borderRadius: 1,
        }}
      />
    </span>
  );
}
